/**
 * Redux witness watchers for users
 * @module services/user/watchers
 */
import { List, fromJS } from 'immutable'
import { isLoggedIn } from 'services/auth'
import { isPageReady } from 'services/pages'
import { getLocationPathname } from 'services/location'
import { SET_AUTH_DATA } from 'services/auth/redux/actions'
import { SET_LOCATION_DATA } from 'services/location/redux/actions'
import { SET_PAGE_READY } from 'services/pages/redux/actions'
import { getConfigDataServer } from 'services/config'
import _get from 'lodash/get'
import { DISMISS_MODAL } from 'services/modal/redux/actions'
import { AMBASSADOR_FORM_MODAL } from 'services/modal'
import {
  getUserHistoryData as getUserHistoryDataAction,
  setUserHistoryProcessing as setUserHistoryProcessingAction,
  updateUserHistoryData as updateUserHistoryDataAction,
  getUserByUid as getUserByUidAction,
  getUserByUuid as getUserByUuidAction,
  getUserByGcsiUserUuid as getUserByGcsiUserUuidAction,
  setUserToNewUser as setUserToNewUserAction,
  SET_USER, GET_USER_HISTORY_DATA,
} from './redux/actions'
import { getUserHistory } from './history'
import {
  GET_USER_CURRENCY_CHANGE_STATUS,
  checkUserCurrencyChangeStatus as checkUserCurrencyChangeStatusAction,
  setUserCurrencyChangeData,
  setUserCurrencyChangePolling,
  setUserCurrencyChangeError,
  CREATE_USER_CURRENCY_CHANGE,
  setUserCurrencyChangeProcessing,
  GET_USER_ACCOUNT_INVOICE_HISTORY_DATA,
  setUserAccountInvoiceHistoryProcessing,
  setUserAccountInvoiceHistoryData,
  setUserAccountInvoiceHistoryDataError,
  GET_USER_ACCOUNT_INVOICE_DATA,
  getUserAccountInvoiceData,
  setUserAccountInvoiceProcessing,
  setUserAccountInvoiceData,
  setUserAccountInvoiceDataError,
  SET_USER_BILLING_SUBSCRIPTION_DATA,
} from './billing-subscription/redux/actions'
import {
  checkUserCurrencyChangeStatus,
  createUserCurrencyChange,
  getUserInvoiceHistory,
  getUserInvoice,
} from './index'
import {
  STORE_KEY_USER_BILLING_SUBSCRIPTION_CURRENCY_CHANGE,
  STORE_KEY_USER_BILLING_SUBSCRIPTION_SUBSCRIPTION,
  STORE_KEY_USER_BILLING_SUBSCRIPTION_INVOICES,
  STORE_KEY_USER_BILLING_SUBSCRIPTION_INVOICE,
} from '../store-keys'
import { getEmailAvailability } from './availability'
import {
  GET_USER_EMAIL_AVAILABILITY,
  resetUserEmailAvailability,
  setUserEmailAvailability,
  setUserEmailAvailabilityProcessing,
} from './availability/redux/actions'

const USER_PAGES = List(['/user-history', '/user-detail', '/user-billing', '/user-invoice'])
const log = console

/**
 * Init user history watcher that will fetch history data when ready
 * @param {Object} param0 The parameters
 * @param {Function} param0.after The witness take every function
 */
export function initUserHistoryDataHandler ({ after } = {}) {
  return after([
    SET_LOCATION_DATA,
    SET_AUTH_DATA,
    SET_PAGE_READY,
    SET_USER,
  ], async ({ state, dispatch }) => {
    const { location } = state
    const page = location.getIn(['data', 'query', 'page']) || 0
    dispatch(getUserHistoryDataAction({ page }))
  }).when(({ state }) => {
    const { location, auth, page } = state
    return getLocationPathname(location) === '/user-history' && isLoggedIn(auth) && isPageReady({ page })
  })
}

/**
 * Get user history data watcher that will fetch history data
 * @param {Object} param0 The parameters
 * @param {Function} param0.after The witness take every function
 */
export function getUserHistoryDataHandler ({ after } = {}) {
  return after([GET_USER_HISTORY_DATA], async ({ state, dispatch, action }) => {
    try {
      const {
        location, user, config, auth,
      } = state
      const server = getConfigDataServer(config, 'auth')
      const uid = location.getIn(['data', 'query', 'uid'])
      const { payload: { page } } = action
      const processing = user.getIn(['userHistory', 'processing'])
      const userInfo = user.getIn(['userDetail', 'data'])
      if (!uid || processing || !userInfo) {
        return
      }
      dispatch(setUserHistoryProcessingAction({ processing: true }))
      const body = {
        userUuid: userInfo.get('uuid'),
        page,
      }
      const history = await getUserHistory({ server, auth, body })
      dispatch(updateUserHistoryDataAction({ processing: false, data: history }))
    } catch (e) {
      dispatch(setUserHistoryProcessingAction({ processing: false }))
    }
  })
}

/**
 * Set user watcher that will fetch user data
 * @param {Object} param0 The parameters
 * @param {Function} param0.after The witness take every function
 */
export function setUserByUidHandler ({ after } = {}) {
  return after([SET_LOCATION_DATA, SET_AUTH_DATA, SET_PAGE_READY], async ({ state, dispatch }) => {
    const { location, user } = state
    const storeKey = 'userDetail'
    const uuid = location.getIn(['data', 'query', 'uuid'])
    const newUser = location.getIn(['data', 'query', 'newUser'])
    const uid = location.getIn(['data', 'query', 'uid'])
    const gcsiUserUuid = location.getIn(['data', 'query', 'gcsiUserUuid'])
    // We don't have location data yet so we can't check queries
    if (!location.get('data')) {
      return
    }
    if (!uuid && !uid && !gcsiUserUuid && !newUser) {
      document.location.href = '/user-search'
    } else if (uuid) {
      const userLoaded = uuid === user.getIn([storeKey, 'data', 'uuid'])
      if (!user.getIn([storeKey, 'processing']) && !userLoaded) {
        dispatch(getUserByUuidAction({ storeKey, uuid }))
      }
    } else if (uid) {
      const userLoaded = uid === user.getIn([storeKey, 'data', 'uid'])
      if (!user.getIn([storeKey, 'processing']) && !userLoaded) {
        dispatch(getUserByUidAction({ storeKey, uid }))
      }
    } else if (gcsiUserUuid) {
      const userLoaded = gcsiUserUuid === user.getIn([storeKey, 'data', 'gcsiUserUuid'])
      if (!user.getIn([storeKey, 'processing']) && !userLoaded) {
        dispatch(getUserByGcsiUserUuidAction({ storeKey, gcsiUserUuid }))
      }
    } else if (newUser && !user.getIn([storeKey, 'data', 'newUser'])) {
      dispatch(setUserToNewUserAction({ storeKey }))
    }
  }).when(({ state }) => {
    const { location, auth, page } = state
    return USER_PAGES.includes(location.getIn(['data', 'pathname'])) && isLoggedIn(auth) && page.get('ready')
  })
}

/**
 * Set user watcher that will request currency change
 * @param {Object} param0 The parameters
 * @param {Function} param0.before The witness take every function
 */
export function createUserCurrencyRequest ({ before } = {}) {
  return before(CREATE_USER_CURRENCY_CHANGE, async ({ state, action, dispatch }) => {
    const { config, auth, userBillingSubscription } = state
    const server = config.getIn(['data', 'servers', 'brooklyn'])
    if (!auth.getIn(['data', 'jwt']) || !server) {
      return
    }

    const billingAccountId = userBillingSubscription.getIn([STORE_KEY_USER_BILLING_SUBSCRIPTION_SUBSCRIPTION, 'data', 'billingAccountId'])
    const billingAccountNumber = userBillingSubscription.getIn([STORE_KEY_USER_BILLING_SUBSCRIPTION_SUBSCRIPTION, 'data', 'billingAccountNumber'])
    const { currency: newCurrency, latamOverride } = action.payload

    dispatch(setUserCurrencyChangeProcessing({
      storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_CURRENCY_CHANGE,
    }))

    const options = {
      auth, server, billingAccountId, billingAccountNumber, newCurrency, latamOverride,
    }
    const currencyChangeData = await createUserCurrencyChange(options)
    if (currencyChangeData.requestId) {
      dispatch(setUserCurrencyChangeData({
        storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_CURRENCY_CHANGE,
        data: fromJS(currencyChangeData),
      }))
      dispatch(setUserCurrencyChangePolling({
        storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_CURRENCY_CHANGE,
      }))
      dispatch(checkUserCurrencyChangeStatusAction({
        requestId: currencyChangeData.requestId,
      }))
    } else {
      dispatch(setUserCurrencyChangeError({
        storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_CURRENCY_CHANGE,
        error: currencyChangeData.errors ? currencyChangeData.errors[0].message : 'Unable to process currency change',
      }))
    }
  })
    .catch((error) => {
      log.error(error, 'Error requesting currency change for user')
      return setUserCurrencyChangeError({
        storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_CURRENCY_CHANGE,
        error: currencyChangeError(error),
      })
    })
}

/**
 * Set user watcher that will fetch currency change status
 * @param {Object} param0 The parameters
 * @param {Function} param0.before The witness take every function
 */
export function getUserCurrencyChangeStatus ({ before } = {}) {
  return before(GET_USER_CURRENCY_CHANGE_STATUS, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const server = config.getIn(['data', 'servers', 'brooklyn'])
    if (!auth.getIn(['data', 'jwt']) || !server) {
      return
    }

    const { requestId } = action.payload
    const options = { auth, server, requestId }
    const data = await checkUserCurrencyChangeStatus(options)

    if (data.done) {
      if (data.success) {
        dispatch(setUserCurrencyChangeData({
          storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_CURRENCY_CHANGE,
          data: fromJS(data),
        }))
        dispatch(setUserCurrencyChangePolling({
          storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_CURRENCY_CHANGE,
          polling: false,
        }))
      } else {
        dispatch(setUserCurrencyChangeData({
          storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_CURRENCY_CHANGE,
          data: fromJS(data),
        }))
        dispatch(setUserCurrencyChangeError({
          storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_CURRENCY_CHANGE,
          error: data.errors,
        }))
        dispatch(setUserCurrencyChangePolling({
          storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_CURRENCY_CHANGE,
          polling: false,
        }))
      }
    } else {
      dispatch(checkUserCurrencyChangeStatusAction({ requestId }))
    }
  })
    .debounce(10000)
    .catch((error) => {
      log.error(error, 'Error polling for currency change status')
      return setUserCurrencyChangeError({
        storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_CURRENCY_CHANGE,
        error: currencyChangeError(error),
      })
    })
}

const currencyChangeError = (error) => {
  const errorBody = JSON.parse((_get(error, ['response', 'text'])))
  const errors = _get(errorBody, 'errors', [])
  return errors.map((it) => (it.message ? it.message : it))
}

/**
 * Watcher to check the email availability.
 *
 * @param {Object} param0 The parameters
 * @param {Function} param0.after The witness take every function
 */
export function watchUserEmailAvailability ({ after } = {}) {
  return after(GET_USER_EMAIL_AVAILABILITY, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const server = config.getIn(['data', 'servers', 'brooklyn'])
    if (!auth.getIn(['data', 'jwt']) || !server) {
      return
    }

    dispatch(setUserEmailAvailabilityProcessing(true))

    try {
      const body = { email: action.payload }
      const options = { auth, server, body }
      const data = await getEmailAvailability(options)

      if (data && Object.prototype.hasOwnProperty.call(data, 'available')) {
        dispatch(setUserEmailAvailability(data.available))
      }
    } catch (error) {
      log.warn(error, 'Error fetching user email availability')
    } finally {
      dispatch(setUserEmailAvailabilityProcessing(false))
    }
  })
}

/**
 * Reset the user email availability when the modal is closed.
 *
 * @param {Object} param0 The parameters
 * @param {Function} param0.before The witness take every function
 */
export function watchResetEmailAvailability ({ before } = {}) {
  return before(DISMISS_MODAL, async ({ state, dispatch }) => {
    const { modal } = state

    if (modal && modal.get('componentName') === AMBASSADOR_FORM_MODAL) {
      dispatch(resetUserEmailAvailability())
    }
  })
}

/**
 * Get user invoice history data watcher that will fetch invoice & taxes data
 * @param {Object} param0 The parameters
 * @param {Function} param0.after The witness take every function
 */
export function watchGetUserInvoiceHistoryData ({ after } = {}) {
  return after([GET_USER_ACCOUNT_INVOICE_HISTORY_DATA], async ({ state, dispatch }) => {
    try {
      const { config, auth, userBillingSubscription } = state
      const server = config.getIn(['data', 'servers', 'brooklyn'])
      const billingAccountId = userBillingSubscription.getIn([STORE_KEY_USER_BILLING_SUBSCRIPTION_SUBSCRIPTION, 'data', 'billingAccountId'])
      const billingAccountNumber = userBillingSubscription.getIn([STORE_KEY_USER_BILLING_SUBSCRIPTION_SUBSCRIPTION, 'data', 'billingAccountNumber'])

      dispatch(setUserAccountInvoiceHistoryProcessing({
        storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_INVOICES,
        processing: true,
      }))

      const invoiceData = await getUserInvoiceHistory({
        server, auth, billingAccountId, billingAccountNumber,
      })
      dispatch(setUserAccountInvoiceHistoryData({
        storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_INVOICES,
        processing: false,
        data: invoiceData,
      }))
    } catch (error) {
      dispatch(setUserAccountInvoiceHistoryDataError({
        storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_INVOICES,
        processing: false,
        error,
      }))
    }
  })
}

/**
 * dispatch action when on invoice page to trigger invoice data watcher
 * @param {Object} param0 The parameters
 * @param {Function} param0.after The witness take every function
 */
export function watchGetUserInvoiceDataPage ({ after } = {}) {
  return after([SET_USER_BILLING_SUBSCRIPTION_DATA], async ({ dispatch }) => {
    dispatch(getUserAccountInvoiceData())
  }).when(({ state }) => {
    const { location, auth, page } = state
    return getLocationPathname(location) === '/user-invoice' && isLoggedIn(auth) && isPageReady({ page })
  })
}

/**
 * Get user invoice data watcher that will fetch invoice & taxes data when on invoice page
 * @param {Object} param0 The parameters
 * @param {Function} param0.after The witness take every function
 */
export function watchGetUserInvoiceData ({ after } = {}) {
  return after([GET_USER_ACCOUNT_INVOICE_DATA], async ({ state, dispatch }) => {
    try {
      const {
        config, auth, userBillingSubscription, location,
      } = state
      const server = config.getIn(['data', 'servers', 'brooklyn'])
      const billingAccountId = userBillingSubscription.getIn([STORE_KEY_USER_BILLING_SUBSCRIPTION_SUBSCRIPTION, 'data', 'billingAccountId'])
      const billingAccountNumber = userBillingSubscription.getIn([STORE_KEY_USER_BILLING_SUBSCRIPTION_SUBSCRIPTION, 'data', 'billingAccountNumber'])
      const invoiceId = location.getIn(['data', 'query', 'invoiceId'])

      dispatch(setUserAccountInvoiceProcessing({
        storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_INVOICE,
        processing: true,
      }))

      const invoiceData = await getUserInvoice({
        server, auth, billingAccountId, billingAccountNumber, invoiceId,
      })
      dispatch(setUserAccountInvoiceData({
        storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_INVOICE,
        processing: false,
        data: invoiceData,
      }))
    } catch (error) {
      dispatch(setUserAccountInvoiceDataError({
        storeKey: STORE_KEY_USER_BILLING_SUBSCRIPTION_INVOICE,
        processing: false,
        error,
      }))
    }
  })
}
